'use client';

/**
 * 광고(파워링크) 유입 변형 — CL fix_4 §2 · fix_7 §1. 판정은 cl-core `isAdVariantWith`(URL 쿼리 또는 유입 캡처 kd_entry.src==='naver_ad').
 *
 * ⚠️ 상세는 ISR(revalidate 3600)이다. 서버가 searchParams 를 읽으면 라우트가 요청마다 렌더로 바뀌어
 *    (B-속도 2026-09-26 · apt-detail-isr.test 잠금) 캐시가 깨진다. 그래서 서버는 «항상 자연 유입 마크업»을
 *    내고, 변형은 하이드레이션 직후 클라이언트가 `location.search` 로 판정해 위치만 바꾼다.
 *    → canonical·색인·서버 HTML 은 변형과 무관(리드폼은 서버 HTML 에 정확히 1회 · CL14).
 *
 *  - <AdLeadSlot position="top">     : 변형일 때만 children(리드폼)을 히어로 바로 아래에 그린다.
 *  - <AdLeadSlot position="natural"> : 변형이 아닐 때 children 을 그린다(서버 HTML 은 항상 여기).
 *  - <AdStickyCta>                   : 변형일 때만 모바일 하단 고정 「분양 정보 안내 신청」 1개.
 * 계측: user_events 에 ad_variant=1(진입 1회 · 고정 버튼 노출/클릭).
 */
import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { isAdVariantWith, kdEntryFromCookie, parseKdEntry, KD_ENTRY_KEY, AD_VARIANT_CTA_LABEL } from '@/lib/cl-core';
import { track } from '@/lib/analytics';
import { trackLeadClick, trackLeadView } from '@/lib/apt/lead-track';
import './cl.css';

function subscribe(): () => void {
  return () => {};
}
/** 현재 URL 쿼리 또는 유입 캡처(fix_7 계약: 쿠키 kd_entry · sessionStorage kd_entry 미러)로 판정. */
function clientSnap(): boolean {
  try {
    let entry = kdEntryFromCookie(document.cookie);
    if (!entry) {
      try { entry = parseKdEntry(window.sessionStorage.getItem(KD_ENTRY_KEY)); } catch { /* 저장소 차단 */ }
    }
    return isAdVariantWith(window.location.search, entry);
  } catch {
    return false;
  }
}
function serverSnap(): boolean {
  return false;
}

/** 광고 유입 변형 여부(서버·첫 하이드레이션은 false). */
export function useAdVariant(): boolean {
  return useSyncExternalStore(subscribe, clientSnap, serverSnap);
}

const SEEN = new Set<string>();

export function AdLeadSlot({ position, siteSlug, children }: { position: 'top' | 'natural'; siteSlug?: string | null; children: ReactNode }) {
  const ad = useAdVariant();
  useEffect(() => {
    if (!ad || position !== 'top') return;
    const k = `${siteSlug ?? '-'}:${typeof window !== 'undefined' ? window.location.pathname : ''}`;
    if (SEEN.has(k)) return;
    SEEN.add(k);
    track('ad_variant_view', 'cl_detail', { ad_variant: 1, site_slug: siteSlug ?? undefined });
  }, [ad, position, siteSlug]);
  if (position === 'top') {
    return ad ? <div className="cl-sec" data-cl-section="lead_form_ad">{children}</div> : null;
  }
  return ad ? null : <>{children}</>;
}

export function AdStickyCta({ targetId, siteSlug, lifecycleStage }: { targetId: string; siteSlug?: string | null; lifecycleStage?: string | null }) {
  const ad = useAdVariant();
  useEffect(() => {
    if (ad) trackLeadView('bottom_bar', { site_slug: siteSlug ?? undefined, lifecycle_stage: lifecycleStage ?? null, ad_variant: 1 });
  }, [ad, siteSlug, lifecycleStage]);
  if (!ad) return null;
  return (
    <div className="cl-adbar" data-cl-cta="ad_sticky">
      <a
        href={`#${targetId}`}
        onClick={(e) => {
          trackLeadClick('bottom_bar', { site_slug: siteSlug ?? undefined, lifecycle_stage: lifecycleStage ?? null, ad_variant: 1 });
          const el = document.getElementById(targetId);
          if (el) {
            e.preventDefault();
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }}
      >
        {AD_VARIANT_CTA_LABEL}
      </a>
    </div>
  );
}
